import React, { useState, useEffect } from "react";
import api from "../../api/api";
import { Plus, Trash2, Send, Layout, AlertTriangle, Edit } from "lucide-react";
import "../../admin_styles/AdminLibrary.css";

export default function AdminFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [newItem, setNewItem] = useState({ title: "", content: "", category: "Health" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [showEditModal, setShowEditModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editUploadProgress, setEditUploadProgress] = useState(0);

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async () => {
    try {
      const res = await api.get("admin/feed");
      setItems(res.data);
    } catch (err) {
      console.error("Failed to fetch feed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", newItem.title);
    formData.append("content", newItem.content);
    formData.append("category", newItem.category);
    if (selectedFile) formData.append("image", selectedFile);

    try {
      await api.post("admin/feed", formData, {
        headers: { 
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      setShowAddModal(false);
      setNewItem({ title: "", content: "", category: "Health" });
      setSelectedFile(null);
      setUploadProgress(0);
      fetchFeed();
    } catch (err) {
      alert("Failed to create post");
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await api.delete(`/admin/feed/${itemToDelete._id}`);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      fetchFeed();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const handleEditClick = (item) => {
    setItemToEdit({ ...item });
    setEditFile(null);
    setEditUploadProgress(0);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", itemToEdit.title);
    formData.append("content", itemToEdit.content);
    formData.append("category", itemToEdit.category);
    if (editFile) formData.append("image", editFile);

    try {
      await api.put(`admin/feed/${itemToEdit._id}`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          setEditUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      setShowEditModal(false);
      setItemToEdit(null);
      setEditFile(null);
      setEditUploadProgress(0);
      fetchFeed();
    } catch (err) {
      alert("Failed to update post");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-section-header">
        <div>
          <h1 className="admin-title">Feed Management</h1>
          <p className="admin-subtitle">Create and manage dashboard announcements</p>
        </div>
        <button className="admin-btn primary" onClick={() => setShowAddModal(true)}>
          <Plus size={18} /> New Feed Post
        </button>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Post Content</th>
              <th>Category</th>
              <th>Engagement</th>
              <th>Created On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" className="text-center">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="4" className="text-center">No feed posts yet</td></tr>
            ) : (
              items.map(item => (
                <tr key={item._id}>
                  <td>
                    <div className="book-cell">
                      <div className="book-icon-sm"><Layout size={14} /></div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600 }}>{item.title}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--admin-muted)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.content}</span>
                      </div>
                    </div>
                  </td>
                  <td><span className="admin-badge blue">{item.category}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span className="admin-badge" style={{ background: '#fff1f2', color: '#e11d48' }} title="Likes">❤️ {item.likes || 0}</span>
                      <span className="admin-badge" style={{ background: '#f0fdf4', color: '#16a34a' }} title="Comments">💬 {item.comments ? item.comments.length + item.comments.reduce((acc, c) => acc + (c.replies ? c.replies.length : 0), 0) : 0}</span>
                    </div>
                  </td>
                  <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="table-actions" style={{ display: 'flex', gap: '8px' }}>
                      <button className="admin-btn secondary sm" onClick={() => handleEditClick(item)} title="Edit Post">
                        <Edit size={16} />
                      </button>
                      <button className="admin-btn danger sm" onClick={() => handleDeleteClick(item)} title="Delete Post">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 600 }}>
            <h3><Send size={20} /> Create New Announcement</h3>
            <form onSubmit={handleAdd}>
              <div className="form-group">
                <label>Title</label>
                <input required type="text" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input type="text" value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea required rows="4" value={newItem.content} onChange={e => setNewItem({...newItem, content: e.target.value})} style={{ width: '100%', borderRadius: 8, border: '1px solid var(--admin-border)', padding: 12 }} />
              </div>
              <div className="form-group">
                <label>Image (Optional)</label>
                <input type="file" accept="image/*" onChange={e => setSelectedFile(e.target.files[0])} />
              </div>
              {uploadProgress > 0 && <div className="upload-progress-bar"><div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div></div>}
              <div className="modal-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="admin-btn primary">Post Announcement</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && itemToEdit && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 600 }}>
            <h3><Edit size={20} style={{ verticalAlign: 'middle', marginRight: 8 }}/> Edit Announcement</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Title</label>
                <input required type="text" value={itemToEdit.title} onChange={e => setItemToEdit({...itemToEdit, title: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input type="text" value={itemToEdit.category} onChange={e => setItemToEdit({...itemToEdit, category: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea required rows="4" value={itemToEdit.content} onChange={e => setItemToEdit({...itemToEdit, content: e.target.value})} style={{ width: '100%', borderRadius: 8, border: '1px solid var(--admin-border)', padding: 12 }} />
              </div>
              <div className="form-group">
                <label>Replace Image (Optional)</label>
                <input type="file" accept="image/*" onChange={e => setEditFile(e.target.files[0])} />
                <small style={{display: 'block', marginTop: 4, color: 'var(--admin-muted)'}}>Leave empty to keep the current image.</small>
              </div>
              {editUploadProgress > 0 && <div className="upload-progress-bar"><div className="progress-fill" style={{ width: `${editUploadProgress}%` }}></div></div>}
              <div className="modal-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="admin-btn primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, textAlign: 'center' }}>
            <div style={{ 
              width: 56, height: 56, borderRadius: '50%', background: '#fff1f2', color: '#e11d48',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
            }}>
              <AlertTriangle size={28} />
            </div>
            <h3 style={{ marginBottom: 8 }}>Move to Recycle Bin?</h3>
            <p style={{ color: 'var(--admin-muted)', fontSize: '0.9rem', marginBottom: 24 }}>
              The announcement "<strong>{itemToDelete?.title}</strong>" will be moved to the recycle bin.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="admin-btn secondary" style={{ flex: 1 }} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="admin-btn danger" style={{ flex: 1 }} onClick={confirmDelete}>Confirm Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
